/**
 * Top-of-page KPI row for the Rehab page. Four tiles per design:
 * Active Rehabs, Budget Used, Avg Progress, Total Properties.
 */
import {
  Building2,
  DollarSign,
  TrendingUp,
  Zap,
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import type { RehabProject } from "@/hooks/use-rehab";

import { deriveBudget, deriveProgressPct, deriveStatus } from "./rehab-status";

function formatCurrency(v: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v);
}

interface RehabKpiRowProps {
  projects: RehabProject[];
}

export function RehabKpiRow({ projects }: RehabKpiRowProps) {
  const buckets = { not_started: 0, in_progress: 0, completed: 0 };
  let totalBudget = 0;
  let totalUsed = 0;
  let progressSum = 0;
  for (const p of projects) {
    buckets[deriveStatus(p)] += 1;
    const b = deriveBudget(p);
    totalBudget += b.budget;
    totalUsed += b.used;
    progressSum += deriveProgressPct(p);
  }
  const avgProgress = projects.length
    ? Math.round(progressSum / projects.length)
    : 0;
  const usedPct =
    totalBudget > 0 ? Math.round((totalUsed / totalBudget) * 100) : 0;

  const tiles = [
    {
      label: "Active Rehabs",
      icon: Zap,
      value: String(buckets.in_progress),
      sub: `${buckets.not_started} not started · ${buckets.completed} completed`,
    },
    {
      label: "Budget Used",
      icon: DollarSign,
      value: formatCurrency(totalUsed),
      sub: totalBudget
        ? `of ${formatCurrency(totalBudget)} planned (${usedPct}%)`
        : "of $0 planned",
    },
    {
      label: "Avg Progress",
      icon: TrendingUp,
      value: `${avgProgress}%`,
      sub: `across ${projects.length} ${projects.length === 1 ? "property" : "properties"}`,
    },
    {
      label: "Total Properties",
      icon: Building2,
      value: String(projects.length),
      sub: "in pipeline rehab tracking",
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {tiles.map((tile) => (
        <Card key={tile.label} className="shadow-soft">
          <CardContent className="flex flex-col gap-1.5 p-4">
            <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <tile.icon className="h-3.5 w-3.5 text-primary" aria-hidden />
              {tile.label}
            </span>
            <span className="text-2xl font-extrabold leading-tight text-foreground">
              {tile.value}
            </span>
            <span className="text-xs text-muted-foreground">{tile.sub}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
